'use client'
import { DateRange } from 'react-date-range'
import { differenceInDays } from 'date-fns'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react'

const DateRangePicker = ({
  dateRange,
  onChange,
  excludeDateIntervals = [],
  disabledDay = null,
  activeInput = null,
  minDate = new Date(),
  classNameSize = '',
  showSummary = true,
}) => {
  const nights = differenceInDays(dateRange[0].endDate, dateRange[0].startDate)

  // Calculate the minimum date based on active input
  const getMinDate = () => {
    if (activeInput === 'checkout' && dateRange[0].startDate) {
      // When selecting checkout, minimum date should be the day after checkin
      const checkinDate = new Date(dateRange[0].startDate)
      checkinDate.setDate(checkinDate.getDate() + 1)
      return checkinDate
    }
    return minDate
  }

  return (
    <>
      <div className={`relative ${classNameSize}`}>
        <DateRange
          ranges={dateRange}
          onChange={onChange}
          moveRangeOnFirstSelection={false}
          months={2}
          direction='horizontal'
          showMonthAndYearPickers={false}
          showDateDisplay={false}
          rangeColors={['#80C7E2']}
          minDate={getMinDate()}
          disabledDates={excludeDateIntervals.flatMap((interval) => {
            const dates = []
            const current = new Date(interval.start)
            while (current <= interval.end) {
              dates.push(new Date(current))
              current.setDate(current.getDate() + 1)
            }
            return dates
          })}
          disabledDay={disabledDay || (() => false)}
          monthDisplayFormat='MMMM yyyy'
          weekdayDisplayFormat='EEEEEE' // Changed to show 2-letter abbreviations
          fixedHeight
          renderStaticRangeLabel={() => ''}
          navigatorRenderer={(focusedDate, onChange, props) => (
            <div className='flex items-center justify-between px-[10px] py-2 cursor-pointer'>
              <button
                type='button'
                onClick={() =>
                  onChange(focusedDate.setMonth(focusedDate.getMonth() - 1))
                }
                className='relative flex items-center justify-center rounded-md cursor-pointer transition-colors duration-150 hover:bg-[#F2FBFE] active:bg-[#E8F7FC]'
              >
                <RiArrowLeftSLine
                  size={20}
                  className='w-[20px] h-[20px] text-[#667085]'
                />
              </button>
              <button
                type='button'
                onClick={() =>
                  onChange(focusedDate.setMonth(focusedDate.getMonth() + 1))
                }
                className='relative flex items-center justify-center rounded-md cursor-pointer transition-colors duration-150 hover:bg-[#F2FBFE] active:bg-[#E8F7FC]'
              >
                <RiArrowRightSLine
                  size={20}
                  className='w-[20px] h-[20px] text-[#667085]'
                />
              </button>
            </div>
          )}
          className={[
            'w-full h-full p-5',
            'daterange',
            '[&_.rdrDateDisplayWrapper]:hidden',
            '[&_.rdrMonthName]:text-center',
            '[&_.rdrMonthName]:text-[14px]',
            '[&_.rdrMonthName]:font-semibold',
            '[&_.rdrMonthName]:text-[#24323E]',
            '[&_.rdrMonthName]:mt-[-40px]',
            '[&_.rdrWeekDays]:mt-[2px]',
            '[&_.rdrWeekDays]:mb-[-10px]',
            '[&_.rdrWeekDay]:w-8', // Fixed width for consistent spacing
            '[&_.rdrWeekDay]:h-6',
            '[&_.rdrWeekDay_span]:font-poppins',
            '[&_.rdrWeekDay_span]:font-medium',
            '[&_.rdrWeekDay_span]:text-[12px]',
            '[&_.rdrWeekDay_span]:leading-[160%]',
            '[&_.rdrWeekDay_span]:tracking-[0]',
            '[&_.rdrWeekDay_span]:text-center',
            '[&_.rdrWeekDay_span]:text-[#5A6973]',
            '[&_.rdrWeekDays]:opacity-100',
            '[&_.rdrDays]:mt-[15px]',
            '[&_.rdrWeekDays]:font-[400]',
            '[&_.rdrWeekDays]:font-poppins',
            '[&_.rdrWeekDays]:leading-[160%]',
            '[&_.rdrWeekDays]:text-[#5A6973]',
            '[&_.rdrWeekDays]:text-center',
            // '[&_.rdrDay_button]:h-[3.7em]',

            '[&_.rdrDayNumber_span]:text-[#24323E]',
            '[&_.rdrDayNumber_span]:top-[7px]',
            '[&_.rdrDayNumber_span]:bottom-[3px]',
            '[&_.rdrDayNumber_span]:font-inter',
            '[&_.rdrDayNumber_span]:font-medium',
            '[&_.rdrDayNumber_span]:text-[15px]',
            '[&_.rdrDayNumber_span]:leading-[20px]',
            '[&_.rdrDayNumber_span]:tracking-[0]',
            '[&_.rdrDayNumber_span]:text-center',
            '[&_.rdrDayPassive]:text-[#C2CAD1]',
            '[&_.rdrMonthAndYearPickers]:hidden',
            '[&_.rdrMonths_horizontal]:flex',
            '[&_.rdrMonth]:min-w-[18rem]',
            '[&_.rdrMonth]:flex-1',
          ].join(' ')}
        />
        <div className='absolute top-5 bottom-0 left-1/2 w-px' style={{ backgroundColor: '#EAF6FB' }}></div>
      </div>

      {showSummary && (
        <div className='px-4 py-2'>
          <div
            className='mx-auto w-fit px-3 py-1 rounded-full text-sm font-medium'
            style={{
              backgroundColor: nights > 0 ? '#80C7E2' : '#F2FBFE',
              color: nights > 0 ? '#FFFFFF' : '#5A6D78',
              boxShadow: nights > 0 ? '0 8px 18px rgba(128, 199, 226, 0.22)' : 'none',
            }}
          >
            {nights > 0
              ? `${nights} night${nights > 1 ? 's' : ''}`
              : 'Select dates'}
          </div>
        </div>
      )}
    </>
  )
}

export default DateRangePicker
